import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import zmodLogo from "../../assets/zmodlogowhite.png";
import "./Layout.css";

// ── Shared nav item definitions ───────────────────────────────────────────────
// mobileOnly: true  → shown only in the mobile hamburger dropdown, not the
//                     desktop sidebar. Used for pages that replace an embedded
//                     widget (e.g. the RTLS map) on small screens.
const NAV_ITEMS = [
  {
    to: "/home",
    label: "Home",
    icon: (
      <svg
        className="nav-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    to: "/module-metrics",
    label: "Module Metrics",
    icon: (
      <svg
        className="nav-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M18 20V10" />
        <path d="M12 20V4" />
        <path d="M6 20v-6" />
      </svg>
    ),
  },
  {
    to: "/personnel-metrics",
    label: "Personnel Metrics",
    icon: (
      <svg
        className="nav-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    to: "/production-milestones",
    label: "Production Milestones",
    icon: (
      <svg
        className="nav-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  // ── Mobile-only: RTLS Map standalone page ────────────────────────────────
  // On desktop, the live map is embedded directly in each page.
  // On mobile (≤480 px) the embedded map is hidden and this nav link
  // takes the user to the dedicated full-screen map page.
  {
    to: "/rtls-map",
    label: "RTLS Map",
    mobileOnly: true,
    icon: (
      <svg
        className="nav-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
        <line x1="9" y1="3" x2="9" y2="18" />
        <line x1="15" y1="6" x2="15" y2="21" />
      </svg>
    ),
  },
];

// Convenience slices used in the two nav sections
const DESKTOP_NAV_ITEMS = NAV_ITEMS.filter((item) => !item.mobileOnly);
const MOBILE_NAV_ITEMS = NAV_ITEMS; // mobile shows everything

// ── Collapse toggle icon (chevrons) ───────────────────────────────────────────
const CollapseIcon = ({ collapsed }) =>
  collapsed ? (
    // Chevron right — click to expand
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ) : (
    // Chevron left — click to collapse
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );

const Layout = () => {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const currentUser = accounts[0];

  // ── Mobile hamburger state ────────────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const sidebarRef = useRef(null);

  // ── Desktop collapse state (persisted to localStorage) ───────────────────
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebarCollapsed", String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  // ── Close mobile menu on route change ────────────────────────────────────
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ── Close mobile menu on outside click/tap ───────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [menuOpen]);

  // ── Prevent body scroll when mobile menu is open ─────────────────────────
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  };

  // Build sidebar class string
  const sidebarClass = [
    "sidebar",
    menuOpen ? "sidebar--open" : "",
    sidebarCollapsed ? "sidebar--collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="layout">
      <aside className={sidebarClass} ref={sidebarRef}>
        {/* ── Header bar ── */}
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={zmodLogo} alt="Z Modular" className="brand-logo" />
          </div>
          {/* Hidden via CSS when collapsed or on mobile */}
          <h2 className="sidebar-title">RTLS Dashboard</h2>
          <span className="env-badge">UAT</span>

          {/* Desktop collapse toggle — hidden on mobile via CSS */}
          <button
            className="desktop-collapse-toggle"
            onClick={toggleCollapsed}
            aria-label={
              sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <CollapseIcon collapsed={sidebarCollapsed} />
          </button>

          {/* Mobile hamburger — hidden on desktop via CSS */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>

        {/* ── Desktop sidebar nav (no mobile-only items) ── */}
        <nav className="sidebar-nav">
          {DESKTOP_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
              // Show label as tooltip when collapsed
              title={sidebarCollapsed ? item.label : undefined}
            >
              {item.icon}
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* ── Mobile dropdown nav (includes RTLS Map link) ── */}
        <div className="mobile-nav-dropdown" aria-hidden={!menuOpen}>
          <nav className="mobile-nav-links">
            {MOBILE_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive
                    ? "mobile-nav-link mobile-nav-link--active"
                    : "mobile-nav-link"
                }
                onClick={() => setMenuOpen(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mobile-nav-footer">
            <div className="mobile-user-row">
              <div className="user-avatar">
                {currentUser?.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="user-details">
                <div className="user-name">{currentUser?.name || "User"}</div>
                <div className="user-email">{currentUser?.username || ""}</div>
              </div>
            </div>
            <button onClick={handleLogout} className="logout-button">
              <svg
                className="logout-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* ── Desktop sidebar footer ── */}
        <div className="sidebar-footer">
          <div
            className="user-info"
            title={
              sidebarCollapsed
                ? `${currentUser?.name || "User"} — ${currentUser?.username || ""}`
                : undefined
            }
          >
            <div className="user-avatar">
              {currentUser?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="user-details">
              <div className="user-name">{currentUser?.name || "User"}</div>
              <div className="user-email">{currentUser?.username || ""}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="logout-button"
            title={sidebarCollapsed ? "Logout" : undefined}
          >
            <svg
              className="logout-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="logout-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile backdrop — closes menu when user taps content area */}
      {menuOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
